/*
  <bible-static-pdp-gallery>
  Media gallery for the Bible static PDP template. Handles images AND videos
  uploaded to the assigned product's media in the Shopify admin.
  - Swipe / drag on the main viewport (no main arrows, like the reference)
  - Thumbnail strip with prev/next paging (desktop) and native swipe (mobile)
  - Thumbnails auto-scroll into view and stay in sync with the active slide
  - Shopify-hosted videos autoplay (muted, looped) while their slide is active,
    pause when it isn't, and can be toggled with the overlay play/pause button
*/
if (!customElements.get('bible-static-pdp-gallery')) {
  customElements.define(
    'bible-static-pdp-gallery',
    class BibleStaticPdpGallery extends HTMLElement {
      connectedCallback() {
        this.viewport = this.querySelector('.bspdp-gallery__viewport');
        this.track = this.querySelector('.bspdp-gallery__track');
        this.slides = Array.from(this.querySelectorAll('.bspdp-gallery__slide'));
        this.thumbs = Array.from(this.querySelectorAll('.bspdp-gallery__thumb'));
        this.thumbsViewport = this.querySelector('.bspdp-gallery__thumbs-viewport');
        this.thumbPrevBtn = this.querySelector('.bspdp-gallery__thumb-nav--prev');
        this.thumbNextBtn = this.querySelector('.bspdp-gallery__thumb-nav--next');

        this.index = 0;
        this.total = this.slides.length;

        this.thumbPrevBtn?.addEventListener('click', () => this.pageThumbs(-1));
        this.thumbNextBtn?.addEventListener('click', () => this.pageThumbs(1));

        this.thumbs.forEach((thumb, i) => {
          thumb.querySelector('.bspdp-gallery__thumb-btn')?.addEventListener('click', () => this.go(i));
        });

        this.slides.forEach((slide) => {
          const video = slide.querySelector('video');
          const toggle = slide.querySelector('.bspdp-gallery__play');
          if (video && toggle) {
            toggle.addEventListener('click', () => (video.paused ? video.play() : video.pause()));
            video.addEventListener('play', () => slide.classList.add('is-playing'));
            video.addEventListener('pause', () => slide.classList.remove('is-playing'));
          }
        });

        this.bindSwipe();
        this.thumbsViewport?.addEventListener('scroll', () => this.updateThumbNavState(), { passive: true });
        this.resizeHandler = () => this.render(false);
        window.addEventListener('resize', this.resizeHandler);

        this.render(true);
      }

      disconnectedCallback() {
        window.removeEventListener('resize', this.resizeHandler);
      }

      go(newIndex) {
        if (this.total === 0) return;
        this.index = Math.max(0, Math.min(newIndex, this.total - 1));
        this.render(true);
      }

      render(scrollThumbIntoView) {
        if (!this.track) return;
        this.track.style.transform = `translateX(-${this.index * 100}%)`;

        this.thumbs.forEach((thumb, i) => {
          thumb.setAttribute('aria-current', i === this.index ? 'true' : 'false');
        });

        this.syncMedia();
        if (scrollThumbIntoView) this.scrollThumbIntoView();
        this.updateThumbNavState();
      }

      syncMedia() {
        this.slides.forEach((slide, i) => {
          const video = slide.querySelector('video');
          const isActive = i === this.index;
          slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
          if (!video) return;
          if (isActive) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      }

      scrollThumbIntoView() {
        const activeThumb = this.thumbs[this.index];
        if (!activeThumb || !this.thumbsViewport) return;

        const viewportRect = this.thumbsViewport.getBoundingClientRect();
        const thumbRect = activeThumb.getBoundingClientRect();
        const isVisible = thumbRect.left >= viewportRect.left && thumbRect.right <= viewportRect.right;

        if (!isVisible) {
          activeThumb.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        }
      }

      pageThumbs(direction) {
        if (!this.thumbsViewport) return;
        this.thumbsViewport.scrollBy({ left: this.thumbsViewport.clientWidth * direction, behavior: 'smooth' });
      }

      updateThumbNavState() {
        if (!this.thumbsViewport || !this.thumbPrevBtn || !this.thumbNextBtn) return;
        const { scrollLeft, scrollWidth, clientWidth } = this.thumbsViewport;
        this.thumbPrevBtn.disabled = scrollLeft <= 2;
        this.thumbNextBtn.disabled = scrollLeft + clientWidth >= scrollWidth - 2;
      }

      bindSwipe() {
        if (!this.viewport) return;

        let startX = 0;
        let currentX = 0;
        let dragging = false;
        const threshold = 40;

        const onStart = (x) => {
          dragging = true;
          startX = x;
          currentX = x;
          this.track.style.transition = 'none';
        };

        const onMove = (x) => {
          if (!dragging) return;
          currentX = x;
          const percentDelta = ((currentX - startX) / this.viewport.clientWidth) * 100;
          this.track.style.transform = `translateX(calc(-${this.index * 100}% + ${percentDelta}%))`;
        };

        const onEnd = () => {
          if (!dragging) return;
          dragging = false;
          this.track.style.transition = '';
          const delta = currentX - startX;

          if (Math.abs(delta) > threshold) {
            this.go(delta < 0 ? this.index + 1 : this.index - 1);
          } else {
            this.render(false);
          }
        };

        this.viewport.addEventListener('touchstart', (e) => onStart(e.touches[0].clientX), { passive: true });
        this.viewport.addEventListener('touchmove', (e) => onMove(e.touches[0].clientX), { passive: true });
        this.viewport.addEventListener('touchend', onEnd);
      }
    }
  );
}
